import React from 'react';
import { Alert, Button, ButtonSizeType, Grid, TreeNavigation, TreeNavigationItem, ButtonBar, Icon, Loader } from '../common';
import WorkflowDetailWrapper from '../common/wrappers/WorkflowDetailWrapper';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import { Validation } from '../../utils/Validation';
import ReadOnlyField from '../common/form-controls/ReadOnly';
import AlertModal from '../common/modals/AlertModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import UpdateAWSPortOpeningRequest from '../../models/viewModels/PortOpeningRequests/UpdateAWSPortOpeningRequest';
import UpdateAWSPortOpeningRequestRule from '../../models/viewModels/PortOpeningRequests/UpdateAWSPortOpeningRequestRule';
import { WorkflowState } from '../../models/enums/WorkflowState';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import './AWSPortOpeningRequestsDetail.css';
import { useNavigate, useParams } from 'react-router-dom';
import {
	AbstractAWSPORForm,
	AbstractAWSPORFormProps,
	AbstractAWSPORFormState,
	IAWSPORStateForm,
} from './components/AbstractAWSPORForm';
import AWSPortOpeningRequest from '../../models/entities/AWSPortOpeningRequests';
import { AWSRulesTable } from './components/AWSRulesTable';
import { ApproverInput, EnvironmentInput } from '../PortOpeningRequests/components';
import AWSPORTypeInput from './components/AWSPORTypeInput';
import AWSPortOpeningRequestsCreate from './AWSPortOpeningRequestsCreate';
import { VirtualPrivateCloud } from '../../models/entities/AzureResources/VirtualPrivateCloud';
import ProgressBarBig from './components/ProgressBarBig';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import { AWSPORScopeInput } from './components/AWSPORScopeInput';
import SelectField from '../common/form-controls/SelectField';

interface AWSPortOpeningRequestsDetailWithParamsProps extends AbstractAWSPORFormProps {}

interface StateForm extends IAWSPORStateForm {}

interface AWSPortOpeningRequestsDetailWithParamsState extends AbstractAWSPORFormState<StateForm> {
	loading: boolean;
	awsPortOpeningRequest?: AWSPortOpeningRequest;
	editMode: boolean;
	matchPatch: string;
	decisionModalVisible: {
		approve?: boolean;
		escalate?: boolean;
		reject?: boolean;
		review?: boolean;
		cancel?: boolean;
		history?: boolean;
	};
	virtualPrivateClouds: VirtualPrivateCloud[];
	secondaryVirtulPrivateClouds: VirtualPrivateCloud[];
	modalComment: string;
	selectedFromExisting: string[];
}

class AWSPortOpeningRequestsDetailWithParams extends AbstractAWSPORForm<
	AWSPortOpeningRequestsDetailWithParamsProps,
	AWSPortOpeningRequestsDetailWithParamsState
> {
	constructor(props) {
		super(props);

		this.state = {
			awsAccount: null,
			secondaryAwsAccount: null,
			loading: true,
			stateForm: {
				environment: null,
				cartaId: [],
				contact: null,
				customerCoordination: null,
				businessJustification: '',
				ruleList: [],
			},
			editMode: false,
			submissionWarned: false,
			operationInProgress: false,
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},
			virtualPrivateClouds: [],
			secondaryVirtulPrivateClouds: [],
			decisionModalVisible: {},
			modalComment: '',
			selectedFromExisting: [],
			matchPatch: null,
			//changeHistoryData: [],
		};

		this.initFormModel();
	}

	initFormModel() {
		super.initFormModel();
		this.formModel.fields.approverId.validation = new Validation({
			required: () => false,
		});

		this.formModel.fields.portOpeningRules.getValue = () =>
			this.state.stateForm.ruleList.map((rule) => new UpdateAWSPortOpeningRequestRule(rule));

		this.formModel.fields.approverId.validation.required = () => this.isRequestWithDOAG();

		this.formModel.fields.appEndorserId.validation.required = () => !this.isRequestWithDOAG();
	}

	async componentWillMount() {
		const id = parseInt(this.props.params.porId);
		const accId = parseInt(this.props.params.accId);
		const secondaryAccId = this.props.params.secondaryAccId != null ? parseInt(this.props.params.accId) : null;
		await this.loadRequest(id, accId, secondaryAccId);
	}

	isRequestWithDOAG = () => !!this.state.awsPortOpeningRequest?.approvingUser;

	loadRequest = async (id: number, accId: any, secondaryAccId: any) => {
		this.setState(
			{
				loading: true,
			},
			async () => {
				try {
					const request = new AWSPortOpeningRequest(
						await this.awsPortOpeningRequestService.getRequestById(id)
					);
					this.setState(
						{
							awsPortOpeningRequest: request,
							loading: false,
							//changeHistoryData,
						},
						this.resetStateForm
					);

					request.workflowInstance.currentWorkflowInstanceState.workflowState == WorkflowState.Review && request.isEditable()
						? await this.handleAppEndorserOptions(request.cartaId[0]?.cartaAppId)
						: this.loadOptions(request);

					if (request.workflowInstance.currentWorkflowInstanceState.workflowState == WorkflowState.Review) {
						if (request.direction.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name) {
							const awsAccount = await this.getAWSAccount(accId);
							const secondaryAwsAccount = await this.getAWSAccount(secondaryAccId);
							const virtualPrivateClouds = awsAccount.vpc;
							const secondaryVirtulPrivateClouds = secondaryAwsAccount.vpc;
							this.setState({
								awsAccount,
								virtualPrivateClouds,
								secondaryAwsAccount,
								secondaryVirtulPrivateClouds,
							});
						} else {
							const awsAccount = await this.getAWSAccount(accId);
							const virtualPrivateClouds = awsAccount.vpc;
							this.setState({ awsAccount, virtualPrivateClouds });
						}
					} else {
						const buildVpcs = (pairs: string[]): VirtualPrivateCloud[] =>
							pairs.map((pair) => {
								const [region, cidr] = pair.split('|');
								return {
									id: 0,
									arn: '',
									name: '',
									region,
									cidr,
									associatedCIDRs: [],
								};
							});

						if (request.direction.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name) {
							const primaryipRegionPair = Array.from(
								new Set(
									request.awsPortOpeningRules.map((rule) => `${rule.region?.name}|${rule.sourceIPs}`)
								)
							);

							const secondaryipRegionPair = Array.from(
								new Set(
									request.awsPortOpeningRules.map(
										(rule) => `${rule.region?.name}|${rule.destinationIPs}`
									)
								)
							);

							const virtualPrivateClouds = buildVpcs(primaryipRegionPair);
							const secondaryVirtulPrivateClouds = buildVpcs(secondaryipRegionPair);

							this.setState({ virtualPrivateClouds, secondaryVirtulPrivateClouds });
						} else {
							var primaryPairIpRergion = [];
							if (request.direction.name == PortOpeningRequestsDirection.InternetToDMZNorth.name) {
								primaryPairIpRergion = Array.from(
									new Set(
										request.awsPortOpeningRules.map((rule) => {
											return `${rule.region?.name}|${rule.destinationIPs}`;
										})
									)
								);
							} else {
								primaryPairIpRergion = Array.from(
									new Set(
										request.awsPortOpeningRules.map((rule) => {
											if (rule.trafficDirection != null && rule.trafficDirection?.name == 'Out')
												return `${rule.region?.name}|${rule.destinationIPs}`;
											else return `${rule.region?.name}|${rule.sourceIPs}`;
										})
									)
								);
							}

							const virtualPrivateClouds = buildVpcs(primaryPairIpRergion);
							this.setState({ virtualPrivateClouds });
						}
					}

					this.setState({
						selectedFromExisting: this.state.stateForm.ruleList.map((rule) => rule.name),
					});
				} catch (error) {
					console.log(error);
					const HTTP_FORBIDDEN_ERROR_CODE = 403;
					if (error.status === HTTP_FORBIDDEN_ERROR_CODE) {
						this.handleEndModal(
							'You are not allowed to see this request.',
							'error',
							5000,
							'/awsPortOpeningRequest/connectedAWS'
						);
					} else {
						this.handleEndModal(
							'The request could not be retrieved.',
							'error',
							10000,
							'/awsPortOpeningRequest/connectedAWS'
						);
					}
				}
			}
		);
	};

	resetStateForm = () => {
		this.setState((prevState) => ({
			stateForm: this.stateFormFromRequest(prevState.awsPortOpeningRequest),
		}));
	};

	
	loadOptions = (por: AWSPortOpeningRequest) => {
		const options = [];
		options.push({
			value: por.appEndorser.customFields.contact_email,
			text: por.appEndorser.customFields.first_name + ' ' + por.appEndorser.customFields.last_name,
		});
		this.setState({ appEndorserOptions: options });
		// switch (por.type) {
		// 	case OnPremPortOpeningRequestsTypes.InternalCertConnection:
		// 		options.push({ value: por.approvingUser.id, text: por.approvingUser.displayName });
		// 		firewallOptions.push({ value: por.firewall, text: por.firewall });
		// 		this.setState({ approverDelegateOptions: options, firewallOptions: firewallOptions });
		// 		break;
		// 	case OnPremPortOpeningRequestsTypes.InternetDMZ:
		// 		options.push({
		// 			value: por.appEndorser.customFields.contact_email,
		// 			text: por.appEndorser.customFields.first_name + ' ' + por.appEndorser.customFields.last_name,
		// 		});
		// 		this.setState({ appEndorserOptions: options });
		// 		break;
	};

	stateFormFromRequest(por: AWSPortOpeningRequest): StateForm {
		const currentState =
			this.state.awsPortOpeningRequest.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		if (currentState === WorkflowState.Review.value || currentState === WorkflowState.Draft.value) {
			const obj = super.stateFormFromRequest(por);
			return {
				...obj,
				ruleList: obj.ruleList.map((rule) => {
					return { ...rule, azureName: undefined };
				}),
			};
		}
		return super.stateFormFromRequest(por);
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName,
		});
	};

	setImplementedRules() {}

	actions = () =>
		!this.state.operationInProgress && (
			<TreeNavigation optionalClass="em-u-clickable">
				{this.state.awsPortOpeningRequest?.isEditable() && (
					<>
						<TreeNavigationItem
							label="Edit"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={this.enableEdit}
						/>
						<br />
						<br />
					</>
				)}
				{(this.state.awsPortOpeningRequest?.canHaveDOAGApproval() ||
					this.state.awsPortOpeningRequest?.canHaveApplicationApproval() ||
					this.state.awsPortOpeningRequest?.canHaveTechnicalDecision() ||
					this.state.awsPortOpeningRequest?.canHaveAccountOwnerDecision(
						this.state.awsPortOpeningRequest.accountOwnerId,
						this.state.awsPortOpeningRequest.secondaryAccountOwnerId,
						this.state.awsPortOpeningRequest?.custodianApproval
					)) && (
					<>
						<TreeNavigationItem
							label="Approve"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('approve');
							}}
						/>
						<br />
						<br />
						<TreeNavigationItem
							label="Reject"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('reject');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.awsPortOpeningRequest?.canHaveEscalateDecision() && (
					<>
						<TreeNavigationItem
							label="Escalate"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('escalate');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.awsPortOpeningRequest?.isCancellable() && (
					<>
						<TreeNavigationItem
							label="Cancel Request"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('cancel');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{(this.state.awsPortOpeningRequest?.canHaveTechnicalDecision() ||
					this.state.awsPortOpeningRequest?.canSendToReview(
						this.state.awsPortOpeningRequest.accountOwnerId,
						this.state.awsPortOpeningRequest?.workflowInstance
					)) && (
					<>
						<TreeNavigationItem
							label="Send to Review"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('review');
							}}
						/>
						<br />
						<br />
					</>
				)}
			</TreeNavigation>
		);

	editActions = (size: ButtonSizeType) => (
		<ButtonBar className="edit-button-bar">
			{!this.state.operationInProgress && (
				<>
					<ButtonBar.Item>
						<Button size={size} variant="secondary" onClick={this.cancelEdit}>
							<Icon size={size} icon="x-filled" />
							<span>Cancel</span>
						</Button>
					</ButtonBar.Item>
					<ButtonBar.Item separator />
				</>
			)}
			<ButtonBar.Item>
				<Button
					size={size}
					variant="primary"
					disabled={!this.formModel.isValid() || this.state.operationInProgress}
					onClick={this.handleSave}
				>
					{this.state.operationInProgress ? (
						<>
							<Loader className="icon-loader em-c-icon--small" />
							<span>Loading...</span>
						</>
					) : (
						<>
							<Icon className="em-c-save-icon" size={size} icon="save-disk" />
							<span>Finish Review</span>
						</>
					)}
				</Button>
			</ButtonBar.Item>
		</ButtonBar>
	);

	enableEdit = async () => {
		this.setState({
			editMode: true,
		});
	};

	cancelEdit = () => {
		this.setState({ editMode: false });
		this.resetStateForm();
	};

	openDecisionModal = (type: 'approve' | 'escalate' | 'reject' | 'review' | 'cancel' | 'history') => {
		this.setState({
			decisionModalVisible: {
				[type]: true,
			},
		});
	};

	closeDecisionModal = () => {
		this.setState({
			decisionModalVisible: {},
			modalComment: '',
		});
	};

	setEmbedAlertSuccess = (message) => {
		this.setState(
			{
				submissionAlert: {
					variant: 'success',
					text: message,
					visible: true,
				},
			},
			() => {
				setTimeout(this.handleSubmissionAlertClose, 5000);
			}
		);
	};

	handleAlert = (variant, text) => {
		this.setState({
			submissionAlert: {
				visible: true,
				variant,
				text,
			},
		});
	};

	decisionModalConfirmationField = () => ({
		label: 'Please, provide the reason.',
		value: this.state.modalComment,
		validation: {
			required: true,
		},
		onChange: (ev) => {
			this.setState({
				modalComment: ev.target.value,
			});
		},
	});

	handleSave = () => {
		const operation = async () => {
			if (this.requiresApproverVerification(this.state.awsPortOpeningRequest?.direction?.name)) {
				const isVerified = await this.approverLevelVerified();
				if (!isVerified) return;
			}
			const model = this.formModel.create(UpdateAWSPortOpeningRequest);
			model.isDraft = false;
			await this.awsPortOpeningRequestService.update(this.state.awsPortOpeningRequest?.id, model);
			this.setState({
				editMode: false,
			});
		};

		this.handleOperationConfirmed({
			operation,
			inProgressMessage: 'Updating request...',
			successMessage: 'Your request was updated successfully.',
		});
	};

	handleApprove = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.awsPortOpeningRequestService.approve(this.state.awsPortOpeningRequest?.id, {
					reason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Approving...',
			successMessage: 'Request approved sucessfully.',
		});
	};

	handleEscalate = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.awsPortOpeningRequestService.escalate(this.state.awsPortOpeningRequest?.id, {
					reviewReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Escalating...',
			successMessage: 'Request escalated sucessfully.',
		});
	};

	handleReject = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.awsPortOpeningRequestService.reject(this.state.awsPortOpeningRequest?.id, {
					rejectionReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Rejecting...',
			successMessage: 'Request rejected sucessfully.',
		});
	};

	handleReview = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.awsPortOpeningRequestService.sendToReview(this.state.awsPortOpeningRequest?.id, {
					reviewReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Sending to review...',
			successMessage: 'Request sent to review successfully.',
		});
	};

	handleCancellation = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.awsPortOpeningRequestService.cancel(this.state.awsPortOpeningRequest?.id, {
					cancellationReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Cancelling...',
			successMessage: 'Request cancelled successfully.',
		});
	};

	handleOperationConfirmed = async ({ operation, inProgressMessage, successMessage }) => {
		this.setSubmissionAlertInProgress(inProgressMessage);
		this.closeDecisionModal();
		try {
			await operation();
			await this.loadRequest(
				this.state.awsPortOpeningRequest?.id,
				this.state.awsPortOpeningRequest.accountId,
				this.state.awsPortOpeningRequest.secondaryAccountId
			);
			this.setEmbedAlertSuccess(successMessage);
		} catch (err) {
			const HTTP_INTERNAL_SERVER_ERROR_CODE = 500;
			if (err.status === HTTP_INTERNAL_SERVER_ERROR_CODE && err?.response?.data.message) {
				await this.loadRequest(
					this.state.awsPortOpeningRequest?.id,
					this.state.awsPortOpeningRequest.accountId,
					this.state.awsPortOpeningRequest.secondaryAccountId
				);
			}
			this.setSubmissionAlertError(
				err?.response?.data.message ?? 'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
				modalComment: '',
			});
		}
	};

	isGLLAwsToGLLAwsScope() {
		return this.state.awsPortOpeningRequest?.direction.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name;
	}

	render() {
		return !this.state.loading &&
			this.state.awsPortOpeningRequest != null &&
			this.state.awsPortOpeningRequest?.isEditable() &&
			this.state.awsPortOpeningRequest?.workflowInstance.currentWorkflowInstanceState.workflowState.value === WorkflowState.Draft.value ?
			(
				<AWSPortOpeningRequestsCreate draftRequest={this.state.awsPortOpeningRequest} />
			) : (
				<>
					<WorkflowDetailWrapper
						title={`AWS Port Opening Request #${this.state.awsPortOpeningRequest?.id}`}
						workflowInstance={this.state.awsPortOpeningRequest?.workflowInstance}
						loading={this.state.loading}
						linkTree={this.getLinkTree()}
						actions={this.state.editMode ? this.editActions('small') : this.actions()}
						fromPOR
						showHistoryButton
						currentApprovers={this.state.awsPortOpeningRequest?.currentApprovers()}
					>
						{this.state.submissionAlert.visible && (
							<Alert
								variant={this.state.submissionAlert.variant}
								onClose={this.handleSubmissionAlertClose}
								body={this.state.submissionAlert.text}
							/>
						)}
						<Grid variant="halves">
							<Grid.Item>
								<ReadOnlyField
									label={
										this.isGLLAwsToGLLAwsScope()
											? "Source account's Name or Id"
											: "Account's Name or Id"
									}
									text={this.state.awsPortOpeningRequest?.accountName}
									copy
								/>
							</Grid.Item>
							{this.isGLLAwsToGLLAwsScope() && (
								<Grid.Item>
									<ReadOnlyField
										label="Destination account's Name or Id"
										text={this.state.awsPortOpeningRequest?.secondaryAccountName}
										copy
									/>
								</Grid.Item>
							)}
						</Grid>
						<Grid variant="3up">
							<Grid.Item>
								<AWSPORTypeInput
									scope={{
										value: this.state.awsPortOpeningRequest?.type?.name,
										disabled: true,
									}}
								/>
							</Grid.Item>
							<Grid.Item>
								<AWSPORScopeInput
									direction={{
										value: this.state.awsPortOpeningRequest?.direction?.name,
										disabled: true,
									}}
									type={this.state.awsPortOpeningRequest?.type?.name}
								/>
							</Grid.Item>
							<Grid.Item>
								<EnvironmentInput
									{...this.stateFormHandler().environment}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
							{this.state.awsPortOpeningRequest?.implementationTicket() !== '' && (
								<Grid.Item>
									<ReadOnlyField
										label="Implementation Ticket"
										text={this.state.awsPortOpeningRequest?.implementationTicket()}
										copy
									/>
								</Grid.Item>
							)}
						</Grid>
						<Grid variant="halves">
							<Grid.Item>
								<UserLookup
									label="Technical contact"
									{...this.stateFormHandler().contact}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
							{this.isRequestWithDOAG() &&
								<Grid.Item>
									<ApproverInput
										{...this.stateFormHandler().approver}
										disabled={this.state.operationInProgress || !this.state.editMode}
										scope={this.state.awsPortOpeningRequest?.direction?.name}
									/>
								</Grid.Item>}
						</Grid>
						{this.state.awsPortOpeningRequest?.cartaId != null && (
							<Grid variant="halves">
								<Grid.Item>
									<CartaInput
										{...this.stateFormHandler().cartaId}
										oneOptionOnly={true}
										disabled={this.state.operationInProgress || !this.state.editMode}
									/>
								</Grid.Item>
								{!this.isRequestWithDOAG() &&
									<Grid.Item>
										<SelectField
											label="Approver (Business Owner, IT Owner, IT Architect)"
											className="full-width"
											{...this.stateFormHandler().appEndorser}
											disabled={
												this.state.operationInProgress ||
												this.stateFormHandler().appEndorser.options.length == 0 ||
												!this.state.editMode ||
												this.stateFormHandler().cartaId.value.length == 0
											}
											isLoading={this.stateFormHandler().appEndorser.options.length === 0}
											note={(this.stateFormHandler().appEndorser.options.length == 0 || this.stateFormHandler().cartaId.value.length == 0) && 'First select the DBM'}
										/>
									</Grid.Item>}
							</Grid>
						)}
						<Grid>
							<Grid.Item>
								<ProgressBarBig
									workflowState={this.state.awsPortOpeningRequest?.workflowInstance.currentWorkflowInstanceState.workflowState}
									por={this.state.awsPortOpeningRequest}
								/>
							</Grid.Item>
						</Grid>
						<Grid />
						<br></br>
						<br></br>
						<TextAreaField
							label="Business Justification"
							{...this.stateFormHandler().businessJustification}
							disabled={this.state.operationInProgress || !this.state.editMode}
						/>
						<Grid />
						<AWSRulesTable
							{...this.stateFormHandler().ruleset}
							porDirection={this.state.awsPortOpeningRequest?.direction}
							porType={this.state.awsPortOpeningRequest?.type}
							readonly={this.state.operationInProgress || !this.state.editMode}
							virtualPrivateClouds={this.state.virtualPrivateClouds}
							secondaryVirtualPrivateClouds={this.state.secondaryVirtulPrivateClouds}
							loadingImplementedRules={false}
							currentWorkflowState={this.state.awsPortOpeningRequest?.workflowInstance.currentWorkflowInstanceState.workflowState.value}
							fromDetails
							selectedFromExisting={this.state.selectedFromExisting}
							setSelectedFromExisting={this.setSelectedFromExisting}
						/>
						{this.state.editMode && this.editActions('small')}
					</WorkflowDetailWrapper>
					<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
					<ConfirmModal
						visible={this.state.decisionModalVisible.approve}
						title={`Approve AWS POR #${this.state.awsPortOpeningRequest?.id}`}
						question="Are you sure you want to approve this request?"
						confirmationField={this.decisionModalConfirmationField()}
						onConfirm={this.handleApprove}
						onCancel={this.closeDecisionModal}
					/>
					<ConfirmModal
						visible={this.state.decisionModalVisible.escalate}
						title={`Escalate AWS POR #${this.state.awsPortOpeningRequest?.id}`}
						question={`Are you sure you want to escalate to 
							${this.state.awsPortOpeningRequest?.workflowInstance.currentWorkflowInstanceState
								.workflowState.value == WorkflowState.PendingNetworkPolicyApproval.value
								? 'Network Policy Solution Architect'
								: 'Cyber Security'
							} this request?`}
						confirmationField={this.decisionModalConfirmationField()}
						onConfirm={this.handleEscalate}
						onCancel={this.closeDecisionModal}
					/>
					<ConfirmModal
						visible={this.state.decisionModalVisible.cancel}
						title={`Cancel AWS POR #${this.state.awsPortOpeningRequest?.id}`}
						question="Are you sure you want to cancel this request? This action cannot be undone."
						confirmationField={this.decisionModalConfirmationField()}
						confirmButton={{
							label: 'Cancel Request',
							props: {
								variant: 'primary',
								color: 'negative',
							},
						}}
						onConfirm={() => this.handleCancellation()}
						onCancel={this.closeDecisionModal}
					/>
					<ConfirmModal
						visible={this.state.decisionModalVisible.reject}
						title={`Reject AWS POR #${this.state.awsPortOpeningRequest?.id}`}
						question="Are you sure you want to reject this request?"
						confirmationField={this.decisionModalConfirmationField()}
						onConfirm={() => this.handleReject()}
						onCancel={this.closeDecisionModal}
					/>
					<ConfirmModal
						visible={this.state.decisionModalVisible.review}
						title={`Confirm Revision for AWS POR #${this.state.awsPortOpeningRequest?.id}`}
						question="Are you sure you want to send this request to review?"
						confirmationField={this.decisionModalConfirmationField()}
						onConfirm={() => this.handleReview()}
						onCancel={this.closeDecisionModal}
					/>
				</>
			);
	}
}

const AWSPortOpeningRequestsDetail = () => {
	return <AWSPortOpeningRequestsDetailWithParams params={useParams()} navigate={useNavigate()} />;
};

export default AWSPortOpeningRequestsDetail;
